import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Collapse,
} from '@mui/material';
import { useStyle } from '../../context/StyleContext';
import tokens from '../../theme/tokens'; // Corrected import path
import CollapsibleSectionHeader from '../shared/CollapsibleSectionHeader'; // Import shared header
import { useLanguage } from '../../localization';
import { getTranslatedStyleName, getTranslatedStyleDescription } from '../../styles/StyleRegistry';

const StyleSelectorComponent = () => {
  const { t } = useLanguage();
  const {
    availableStyles,
    selectedStyleId,
    setSelectedStyleId,
  } = useStyle();

  const themeTokens = tokens; // Use the imported tokens
  const [isStyleSectionOpen, setIsStyleSectionOpen] = useState(true); // Add state for collapse

  if (!availableStyles || availableStyles.length === 0) {
    return <Typography>{t('messages.noStylesAvailable')}</Typography>;
  }

  // Reusable function to render a style option card (similar to DesignSelector)
  const renderStyleOption = (style) => {
    const isSelected = selectedStyleId === style.id;
    const iconColor = isSelected ? themeTokens.colors.primary.main : themeTokens.colors.text.secondary;
    
    return (
      <Grid item xs={12} sm={4} key={style.id}> {/* Use style.id as key */}
        <Paper
          elevation={isSelected ? 4 : 1}
          onClick={() => setSelectedStyleId(style.id)}
          sx={{
            p: themeTokens.spacing[3],
            cursor: 'pointer',
            border: `1px solid ${isSelected ? themeTokens.colors.primary.main : themeTokens.colors.borders.default}`,
            borderWidth: isSelected ? '2px' : '1px',
            minHeight: '70px',
            height: '100%',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'flex-start',
            textAlign: 'left',
            transition: `all ${tokens.transitions.durations.quick} ease`,
            backgroundColor: isSelected ? themeTokens.colors.alpha.black[50] : themeTokens.colors.background.paper,
            borderRadius: themeTokens.radius.base,
            '&:hover': {
              borderColor: isSelected ? themeTokens.colors.primary.main : themeTokens.colors.primary.light,
              boxShadow: isSelected ? themeTokens.shadows.md : themeTokens.shadows.base,
              backgroundColor: isSelected ? themeTokens.colors.alpha.black[50] : themeTokens.colors.neutral[50],
            }
          }}
        >
          {/* Render the icon component directly */}
          {style.icon && React.cloneElement(style.icon, { sx: { fontSize: themeTokens.spacing[8], mx: themeTokens.spacing[2], color: iconColor } })}
          <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, mr: themeTokens.spacing[2] }}>
            <Typography variant="body2" fontWeight={themeTokens.typography.fontWeights.medium} lineHeight={themeTokens.typography.lineHeights.tight} sx={{ fontSize: themeTokens.typography.fontSizes.sm, color: themeTokens.colors.text.primary }}>
              {getTranslatedStyleName(style.id, t)}
            </Typography>
            <Typography variant="caption" color={themeTokens.colors.text.secondary} lineHeight={themeTokens.typography.lineHeights.tight} sx={{ fontSize: themeTokens.typography.fontSizes.xs }}>
              {getTranslatedStyleDescription(style.id, t)}
            </Typography>
          </Box>
        </Paper>
      </Grid>
    );
  };

  return (
    <Box>
      <CollapsibleSectionHeader 
        title={t('settingsPanel.style.title')}
        isOpen={isStyleSectionOpen}
        toggleOpen={() => setIsStyleSectionOpen(!isStyleSectionOpen)}
        sx={{ mb: themeTokens.spacing[1] }}
      />
      <Collapse in={isStyleSectionOpen}>
        <Grid container spacing={themeTokens.spacing[3]} sx={{ pt: themeTokens.spacing[1] }}>
          {availableStyles.map(renderStyleOption)}
        </Grid>
      </Collapse>
    </Box>
  );
}

const StyleSelector = React.memo(StyleSelectorComponent);

export default StyleSelector; 